import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useProcessCounts } from '@/hooks/useProcessCounts';
import { Button } from '@/components/ui/button';
import { useTheme } from 'next-themes';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Wallet,
  CreditCard,
  Settings,
  LogOut,
  Building2,
  Sun,
  Moon,
  User,
  PlusCircle,
  Loader2,
  Shield,
  Home,
  ClipboardList,
  ChevronDown,
  TrendingUp,
  BarChart3,
  Target,
  Bell,
  AlertTriangle,
  Clock,
  Inbox,
  Calculator,
  CheckSquare,
  FileSignature,
  Users,
  FlaskConical,
  Landmark,
  Gem,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { cs } from 'date-fns/locale';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  badgeKey?: keyof ReturnType<typeof useProcessCounts>['counts'];
}

interface NavSection {
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

const adminNavigation: Record<string, NavSection> = {
  pujcovani: {
    label: 'Půjčování',
    icon: Wallet,
    items: [
      { name: '1. Příjem', href: '/process/intake', icon: Inbox, badgeKey: 'intake' },
      { name: '2. Scoring', href: '/process/scoring', icon: Calculator, badgeKey: 'scoring' },
      { name: '3. Schvalování', href: '/process/approval', icon: CheckSquare, badgeKey: 'approval' },
      { name: '4. Smlouvy', href: '/process/contracts', icon: FileSignature, badgeKey: 'contracts' },
      { name: '5. Aktivní', href: '/loans', icon: Wallet, badgeKey: 'active' },
      { name: 'Platby', href: '/payments', icon: CreditCard },
      { name: 'Banka', href: '/bank-transactions', icon: Building2 },
      { name: 'Klienti', href: '/clients', icon: Users },
    ],
  },
  analytika: {
    label: 'Finance & Analytika',
    icon: BarChart3,
    items: [
      { name: 'Přehled', href: '/finance', icon: TrendingUp },
      { name: 'Výsledky', href: '/financials', icon: Gem },
      { name: 'Portfolio klientů', href: '/portfolio', icon: Users },
    ],
  },
  kapital: {
    label: 'Kapitál',
    icon: Landmark,
    items: [
      { name: 'Přehled', href: '/capital', icon: Landmark },
      { name: 'Investice', href: '/capital/investments', icon: TrendingUp },
      { name: 'Zdroje & protistrany', href: '/capital/sources', icon: Users },
    ],
  },
  system: {
    label: 'Systém',
    icon: Settings,
    items: [
      { name: 'Uživatelé', href: '/users', icon: Shield },
      { name: 'Úkoly', href: '/todos', icon: CheckSquare },
      { name: 'System Health', href: '/system-health', icon: Activity },
      { name: 'Audit Log', href: '/audit-log', icon: ClipboardList },
      { name: 'Decision Audit', href: '/decision-audit', icon: Target },
      { name: 'DE Sandbox', href: '/sandbox', icon: FlaskConical },
      { name: 'AI Analýza Sandbox', href: '/ai-analysis-sandbox', icon: FlaskConical },
      { name: 'Pravidla DE', href: '/rules', icon: ClipboardList },
      { name: 'Nastavení', href: '/settings', icon: Settings },
    ],
  },
};

// User navigation organized into 3 blocks: Finance, Akce, Účet
interface UserNavSection {
  label: string;
  items: NavItem[];
}

const userNavSections: Record<string, UserNavSection> = {
  finance: {
    label: 'FINANCE',
    items: [
      { name: 'Dashboard', href: '/user-dashboard', icon: Home },
      { name: 'Moje zápůjčky', href: '/my-loans', icon: Wallet },
    ],
  },
  akce: {
    label: 'AKCE',
    items: [
      { name: 'Nová žádost', href: '/new-loan-request', icon: PlusCircle },
    ],
  },
  ucet: {
    label: 'ÚČET',
    items: [
      { name: 'Můj profil', href: '/profile', icon: User },
    ],
  },
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(amount);

export function Sidebar() {
  const { signOut } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const { counts: processCounts } = useProcessCounts();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  // Track which sections are open
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    pujcovani: true,
    analytika: false,
    system: false,
  });

  // Fetch company branding
  const { data: loanConfig } = useQuery({
    queryKey: ['loan-config-branding'],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('loan_config')
        .select('company_name, company_logo_url')
        .maybeSingle();
      return data;
    },
  });

  // Fetch overdue payments for notifications
  const { data: overduePayments } = useQuery({
    queryKey: ['notifications-overdue'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await (supabase as any)
        .from('payments')
        .select(`
          id, amount, due_date,
          loans!inner(
            loan_requests!inner(
              applicants!inner(name)
            )
          )
        `)
        .neq('status', 'paid')
        .lt('due_date', today)
        .order('due_date', { ascending: true })
        .limit(10);
      
      return data || [];
    },
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  // Fetch pending loan requests for notifications
  const { data: pendingRequests } = useQuery({
    queryKey: ['notifications-pending'],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('loan_requests')
        .select(`
          id, amount_requested, created_at,
          applicants!inner(id, name)
        `)
        .in('status', ['new', 'review'])
        .order('created_at', { ascending: false })
        .limit(10);
      
      return data || [];
    },
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  // Fetch pending guest applications for notifications
  const { data: pendingGuestApps } = useQuery({
    queryKey: ['notifications-guest-pending'],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('guest_applications')
        .select('id, full_name, amount_requested, created_at')
        .in('status', ['pending', 'reviewing'])
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  const totalNotifications = (overduePayments?.length || 0) + (pendingRequests?.length || 0) + (pendingGuestApps?.length || 0);

  // Auto-expand section containing current route
  useEffect(() => {
    if (!isAdmin) return;
    
    for (const [sectionKey, section] of Object.entries(adminNavigation)) {
      if (section.items.some(item => item.href === location.pathname || location.pathname.startsWith(item.href + '/'))) {
        setOpenSections(prev => ({ ...prev, [sectionKey]: true }));
        break;
      }
    }
  }, [location.pathname, isAdmin]);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const companyName = loanConfig?.company_name || 'LoanAdmin';
  const companyLogo = loanConfig?.company_logo_url;

  const handleNotificationClick = (type: 'overdue' | 'pending', id?: string) => {
    setNotificationsOpen(false);
    if (type === 'overdue') {
      navigate('/payments');
    } else if (type === 'pending' && id) {
      navigate('/process/intake');
    } else {
      navigate('/process/intake');
    }
  };

  const getBadgeVariant = (count: number): 'default' | 'secondary' | 'destructive' => {
    if (count === 0) return 'secondary';
    if (count >= 5) return 'destructive';
    return 'default';
  };

  const renderNavItem = (item: NavItem, index: number) => {
    const badgeCount = item.badgeKey ? processCounts[item.badgeKey] : undefined;
    
    return (
      <NavLink
        key={item.name}
        to={item.href}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ease-out',
            isActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground hover:translate-x-1'
          )
        }
        style={{ animationDelay: `${index * 0.03}s` }}
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-sidebar-primary rounded-r-full animate-scale-in" />
            )}
            <item.icon className={cn(
              "h-4 w-4 transition-transform duration-200",
              "group-hover:scale-110"
            )} />
            <span className="tracking-wide flex-1">{item.name}</span>
            {badgeCount !== undefined && badgeCount > 0 && (
              <Badge 
                variant={getBadgeVariant(badgeCount)}
                className="h-5 min-w-5 flex items-center justify-center text-xs px-1.5"
              >
                {badgeCount}
              </Badge>
            )}
          </>
        )}
      </NavLink>
    );
  };

  const renderNotificationCenter = () => (
    <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "relative w-full justify-start gap-3 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200 group",
            totalNotifications > 0 && "text-sidebar-foreground"
          )}
        >
          <div className="relative">
            <Bell className={cn(
              "h-5 w-5 transition-transform duration-200",
              totalNotifications > 0 && "animate-pulse"
            )} />
            {totalNotifications > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                {totalNotifications > 9 ? '9+' : totalNotifications}
              </span>
            )}
          </div>
          <span className="tracking-wide">Upozornění</span>
          {totalNotifications > 0 && (
            <Badge variant="destructive" className="ml-auto text-xs">
              {totalNotifications}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        side="right" 
        align="start"
        sideOffset={8}
      >
        <div className="p-3 border-b">
          <h4 className="font-semibold text-sm">Upozornění</h4>
          <p className="text-xs text-muted-foreground">
            {totalNotifications === 0 
              ? 'Žádná nová upozornění' 
              : `${totalNotifications} čekajících upozornění`
            }
          </p>
        </div>
        <ScrollArea className="max-h-80">
          {/* Overdue Payments Section */}
          {overduePayments && overduePayments.length > 0 && (
            <div>
              <div className="px-3 py-2 bg-destructive/10">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-xs font-medium">
                    Po splatnosti ({overduePayments.length})
                  </span>
                </div>
              </div>
              <div className="divide-y">
                {overduePayments.map((payment: any) => (
                  <button
                    key={payment.id}
                    className="w-full px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => handleNotificationClick('overdue')}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {payment.loans?.loan_requests?.applicants?.name || 'Neznámý'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(payment.amount)}
                        </p>
                      </div>
                      <span className="text-xs text-destructive whitespace-nowrap">
                        {formatDistanceToNow(new Date(payment.due_date), { addSuffix: true, locale: cs })}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Pending Requests Section */}
          {pendingRequests && pendingRequests.length > 0 && (
            <div>
              <div className="px-3 py-2 bg-warning/10">
                <div className="flex items-center gap-2 text-warning">
                  <Clock className="h-4 w-4" />
                  <span className="text-xs font-medium">
                    Čekající žádosti ({pendingRequests.length})
                  </span>
                </div>
              </div>
              <div className="divide-y">
                {pendingRequests.map((request: any) => (
                  <button
                    key={request.id}
                    className="w-full px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => handleNotificationClick('pending', request.applicants?.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {request.applicants?.name || 'Neznámý'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(request.amount_requested)}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDistanceToNow(new Date(request.created_at), { addSuffix: true, locale: cs })}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Guest Applications Section */}
          {pendingGuestApps && pendingGuestApps.length > 0 && (
            <div>
              <div className="px-3 py-2 bg-primary/10">
                <div className="flex items-center gap-2 text-primary">
                  <Inbox className="h-4 w-4" />
                  <span className="text-xs font-medium">
                    Noví žadatelé ({pendingGuestApps.length})
                  </span>
                </div>
              </div>
              <div className="divide-y">
                {pendingGuestApps.map((app: any) => (
                  <button
                    key={app.id}
                    className="w-full px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => handleNotificationClick('pending')}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {app.full_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(app.amount_requested)}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDistanceToNow(new Date(app.created_at), { addSuffix: true, locale: cs })}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Empty State */}
          {totalNotifications === 0 && (
            <div className="p-6 text-center">
              <Bell className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                Vše je v pořádku!
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Žádné opožděné platby ani čekající žádosti.
              </p>
            </div>
          )}
        </ScrollArea>
        
        {totalNotifications > 0 && (
          <>
            <Separator />
            <div className="p-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => {
                  setNotificationsOpen(false);
                  navigate('/');
                }}
              >
                Zobrazit vše na Dashboard
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );

  const renderAdminNavigation = () => (
    <div className="space-y-2">
      {Object.entries(adminNavigation).map(([key, section]) => {
        const isOpen = openSections[key];
        const hasActiveItem = section.items.some(item => 
          item.href === location.pathname || location.pathname.startsWith(item.href + '/')
        );
        
        // Calculate total badge count for section
        const sectionBadgeCount = section.items.reduce((sum, item) => {
          if (item.badgeKey && processCounts[item.badgeKey]) {
            return sum + processCounts[item.badgeKey];
          }
          return sum;
        }, 0);
        
        return (
          <Collapsible
            key={key}
            open={isOpen}
            onOpenChange={() => toggleSection(key)}
          >
            <CollapsibleTrigger asChild>
              <button
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                  hasActiveItem
                    ? "bg-sidebar-accent/30 text-sidebar-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/30 hover:text-sidebar-foreground"
                )}
              >
                <div className="flex items-center gap-3">
                  <section.icon className="h-5 w-5" />
                  <span className="font-semibold tracking-wide">{section.label}</span>
                  {!isOpen && sectionBadgeCount > 0 && (
                    <Badge variant="secondary" className="h-5 min-w-5 text-xs px-1.5">
                      {sectionBadgeCount}
                    </Badge>
                  )}
                </div>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    isOpen && "rotate-180"
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-2 pt-1 space-y-0.5 animate-accordion-down">
              {section.items.map((item, index) => renderNavItem(item, index))}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );

  const renderUserNavigation = () => (
    <div className="space-y-6">
      {Object.entries(userNavSections).map(([key, section]) => (
        <div key={key}>
          <p className="px-3 mb-2 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
            {section.label}
          </p>
          <div className="space-y-1">
            {section.items.map((item, index) => renderNavItem(item, index))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex h-screen w-64 flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-16 items-center gap-3 px-6 border-b border-sidebar-border">
        {companyLogo ? (
          <img 
            src={companyLogo} 
            alt={companyName} 
            className="w-10 h-10 rounded-xl object-cover transition-transform duration-300 hover:scale-105"
          />
        ) : (
          <div className="w-10 h-10 bg-sidebar-primary rounded-xl flex items-center justify-center transition-transform duration-300 hover:scale-105">
            <Building2 className="w-5 h-5 text-sidebar-primary-foreground" />
          </div>
        )}
        <span className="font-display text-lg font-semibold tracking-tight truncate">{companyName}</span>
      </div>
      
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {roleLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-sidebar-foreground/50" />
          </div>
        ) : isAdmin ? (
          <>
            {renderAdminNavigation()}
            
            {/* Notification Center - only for admins */}
            <Separator className="my-4" />
            {renderNotificationCenter()}
          </>
        ) : (
          renderUserNavigation()
        )}
      </nav>

      <div className="p-3 border-t border-sidebar-border space-y-2">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200 group"
          onClick={toggleTheme}
        >
          {theme === 'dark' ? (
            <Sun className="h-5 w-5 transition-transform duration-200 group-hover:rotate-12" />
          ) : (
            <Moon className="h-5 w-5 transition-transform duration-200 group-hover:-rotate-12" />
          )}
          <span className="tracking-wide">{theme === 'dark' ? 'Světlý režim' : 'Tmavý režim'}</span>
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-sidebar-foreground/70 hover:text-destructive hover:bg-destructive/10 transition-all duration-200 group"
          onClick={signOut}
        >
          <LogOut className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
          <span className="tracking-wide">Odhlásit se</span>
        </Button>
      </div>
    </div>
  );
}
